import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ShoppingBag, AlertTriangle, ArrowRight, ChevronDown, ChevronUp,
  RotateCcw, Check, CalendarDays, Plus, Trash2, X, Calculator, BookOpen, ClipboardPaste,
} from "lucide-react";
import MOShell, {
  useMOAccess, isWritable, LoadingBlock, NoAccessScreen,
} from "@/components/masterOrder/MOShell";
import {
  displayOrderNumber, getShoppingList, patchShoppingState, resetShoppingPurchased,
  createManualPurchase, deleteManualPurchase, listIngredients, listPackagingItems,
  updateOrderItem, listRecipes, getRecipe, unitLabel,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";
import { createWorkbookEntry } from "@/lib/workbookApi";
import { parseRecipe } from "./MORecipeRecalc";

/* ------------------------------ helpers -------------------------------- */

const orderItemLabel = (item) => [
  `Заказ №${displayOrderNumber(item.order_number)}`,
  item.order_item_position ? `Позиция ${item.order_item_position}` : null,
  item.product_type_name,
  item.size_display,
  item.flavor_composition,
].filter(Boolean).join(" · ");

const todayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};
const addDaysISO = (isoDate, days) => {
  const d = new Date(isoDate + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};
const RU_MONTHS = ["янв","февр","мар","апр","мая","июня","июля","авг","сент","окт","нояб","дек"];
const fmtShort = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate()} ${RU_MONTHS[d.getMonth()]}`;
};
const rangeLabel = (start, end) =>
  start === end ? fmtShort(start) : `${fmtShort(start)} — ${fmtShort(end)}`;

/** Human-friendly unit conversion from backend normalized (g/ml/pcs/cm) values.
 *  Returns { value: Number, unit: string } — value is rounded pragmatically.
 */
function humanize(baseUnit, normalizedQty) {
  const n = Number(normalizedQty);
  if (!Number.isFinite(n)) return { value: 0, unit: baseUnit };
  const abs = Math.abs(n);
  if (baseUnit === "g") {
    if (abs >= 1000) return { value: n / 1000, unit: "кг" };
    return { value: n, unit: "г" };
  }
  if (baseUnit === "ml") {
    if (abs >= 1000) return { value: n / 1000, unit: "л" };
    return { value: n, unit: "мл" };
  }
  if (baseUnit === "cm") {
    if (abs >= 100) return { value: n / 100, unit: "м" };
    return { value: n, unit: "см" };
  }
  if (baseUnit === "pcs") return { value: n, unit: "шт." };
  if (baseUnit === "pack") return { value: n, unit: "уп." };
  return { value: n, unit: unitLabel(baseUnit) };
}
/** Pragmatic number formatter: whole → no decimals; 1..1000 → up to 2 dp; small → 3 sig. */
function fmtNum(n) {
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return "0";
  if (Math.abs(n) >= 100 && Number.isInteger(n * 100)) {
    return n.toLocaleString("ru-RU", { maximumFractionDigits: 1 });
  }
  if (Math.abs(n) < 0.01) return n.toPrecision(2);
  const rounded = Math.round(n * 100) / 100;
  return rounded.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
}
function humanQty(baseUnit, normalizedQty) {
  const { value, unit } = humanize(baseUnit, normalizedQty);
  return `${fmtNum(value)} ${unit}`;
}
/** Convert a human-facing value+unit back to the base unit (for PATCH). */
function toBaseUnit(baseUnit, humanValue, humanUnit) {
  const v = Number(humanValue);
  if (!Number.isFinite(v)) return null;
  if (baseUnit === "g") return humanUnit === "кг" ? v * 1000 : v;
  if (baseUnit === "ml") return humanUnit === "л" ? v * 1000 : v;
  if (baseUnit === "cm") return humanUnit === "м" ? v * 100 : v;
  return v; // pcs
}
/** Preferred input unit for editing "У меня есть" — matches the display unit
 *  that the required quantity is shown in, so numbers feel natural. */
function preferredInputUnit(baseUnit, referenceQty) {
  const { unit } = humanize(baseUnit, referenceQty);
  return unit;
}

/* ------------------------------- period -------------------------------- */

const PRESETS = [
  { id: "today",  label: "Сегодня",  days: 0 },
  { id: "3d",     label: "3 дня",    days: 2 },
  { id: "7d",     label: "7 дней",   days: 6 },
  { id: "14d",    label: "14 дней",  days: 13 },
  { id: "custom", label: "Свой период", days: null },
];

function PeriodPicker({ preset, onPreset, start, end, onCustom, ordersCount }) {
  return (
    <div className="rounded-2xl bg-white px-4 py-3 md:px-5 md:py-4"
         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
         data-testid="mo-purchases-period">
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((p) => {
          const active = preset === p.id;
          return (
            <button
              key={p.id}
              onClick={() => onPreset(p.id)}
              className="h-9 px-3.5 rounded-xl text-[12.5px] transition"
              style={{
                background: active ? "#4A3728" : "#F7F1E7",
                color: active ? "#F7EFE0" : "#4A3728",
                boxShadow: active ? "none" : "inset 0 0 0 1px #EFE4D0",
              }}
              data-testid={`mo-period-${p.id}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {preset === "custom" && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[12.5px]" style={{ color: "#4A3728" }}>
          <label className="flex items-center gap-1.5">
            <CalendarDays size={13} color="#8B6F4B" />
            <span>С</span>
            <input type="date" value={start} onChange={(e) => onCustom(e.target.value, end)}
                   className="h-9 px-2 rounded-lg bg-white outline-none text-[12.5px]"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                   data-testid="mo-period-custom-start" />
          </label>
          <label className="flex items-center gap-1.5">
            <span>по</span>
            <input type="date" value={end} onChange={(e) => onCustom(start, e.target.value)}
                   className="h-9 px-2 rounded-lg bg-white outline-none text-[12.5px]"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                   data-testid="mo-period-custom-end" />
          </label>
        </div>
      )}

      <div className="mt-3 text-[12.5px]" style={{ color: "#8B6F4B" }} data-testid="mo-period-summary">
        <span style={{ color: "#4A3728" }}>{rangeLabel(start, end)}</span>
        <span> · </span>
        <span>
          {ordersCount === 0 ? "нет заказов" : `${ordersCount} ${
            ordersCount === 1 ? "заказ" : ordersCount < 5 ? "заказа" : "заказов"
          }`}
        </span>
      </div>
    </div>
  );
}

/* ------------------------ unresolved warning block --------------------- */

function UnresolvedBlock({ items, canWrite, onCalculate, onExclude }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;

  const n = items.length;
  const noun = n === 1 ? "позиции" : "позиций";

  return (
    <div className="rounded-2xl px-4 py-4 md:px-5 md:py-4 mb-4"
         style={{ background: "#FDF3D9", boxShadow: "inset 0 0 0 1px #E9D28F" }}
         data-testid="mo-purchases-unresolved">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full grid place-items-center shrink-0"
             style={{ background: "#F1D07C", color: "#5A3E10" }}>
          <AlertTriangle size={16} strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] leading-tight" style={{ color: "#5A3E10" }}>
            Для {n} {noun} пока не рассчитаны продукты
          </div>
          <div className="text-[11.5px] mt-0.5" style={{ color: "#8A6B2B" }}>
            Рассчитайте их или исключите из списка покупок.
          </div>
        </div>
        <button onClick={() => setOpen((v) => !v)}
                className="h-9 px-3 rounded-xl text-[12px] shrink-0 flex items-center gap-1"
                style={{ background: "#FFFFFF", color: "#5A3E10",
                         boxShadow: "inset 0 0 0 1px #E9D28F" }}
                data-testid="mo-purchases-unresolved-toggle">
          {open ? "Скрыть" : "Посмотреть"}
          {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      {open && (
        <ul className="mt-3 space-y-2" data-testid="mo-purchases-unresolved-list">
          {items.map((it) => (
              <li key={`${it.order_id}-${it.order_item_id}`}>
                <div className="w-full flex flex-col md:flex-row md:items-center gap-3 rounded-xl bg-white px-3.5 py-3"
                  style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}
                  data-testid="mo-purchases-unresolved-row"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] truncate">
                      {orderItemLabel(it)}
                    </div>
                    <div className="text-[11.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
                      Продукты ещё не рассчитаны
                    </div>
                  </div>
                  {canWrite && <div className="flex flex-wrap gap-2 shrink-0">
                    <button onClick={() => onCalculate(it)} className="h-9 px-3.5 rounded-xl text-[12px] flex items-center gap-1.5"
                            style={{ background: "#4A3728", color: "#F7EFE0" }} data-testid="mo-purchases-calculate-item">
                      <Calculator size={13} /> Рассчитать
                    </button>
                    <button onClick={() => onExclude(it)} className="h-9 px-3.5 rounded-xl text-[12px]"
                            style={{ color: "#6E5846", boxShadow: "inset 0 0 0 1px #E4D9C4" }} data-testid="mo-purchases-exclude-item">
                      Не учитывать в списке покупок
                    </button>
                  </div>}
                </div>
              </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ExcludedBlock({ items, canWrite, onRestore }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  return (
    <div className="mb-4 rounded-2xl bg-white px-4 py-3.5" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="mo-purchases-excluded">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 text-left">
        <span className="text-[12.5px]" style={{ color: "#6E5846" }}>Не учитываются в списке: {items.length}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && <div className="mt-3 space-y-2">
        {items.map((it) => <div key={it.order_item_id} className="flex flex-col md:flex-row md:items-center gap-2 rounded-xl px-3 py-2.5" style={{ background: "#FAF7F1" }}>
          <div className="flex-1 min-w-0 text-[12.5px]" style={{ color: "#4A3728" }}>{orderItemLabel(it)}</div>
          {canWrite && <button onClick={() => onRestore(it)} className="h-8 px-3 rounded-xl text-[12px]" style={{ background: "#FFFFFF", color: "#4A3728", boxShadow: "inset 0 0 0 1px #E4D9C4" }} data-testid="mo-purchases-restore-item">Вернуть в список</button>}
        </div>)}
      </div>}
    </div>
  );
}

function CalculateProductsModal({ item, onClose, onSaved }) {
  const nav = useNavigate();
  const [mode, setMode] = useState("menu");
  const [entries, setEntries] = useState([]);
  const [ingredientNames, setIngredientNames] = useState({});
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [busyRecipeId, setBusyRecipeId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [paste, setPaste] = useState("");
  const [parsed, setParsed] = useState(null);

  const savePayload = async (payload) => {
    const rows = payload.recalculated_ingredients?.length ? payload.recalculated_ingredients : payload.source_ingredients;
    if (!Array.isArray(rows) || !rows.some((row) => row?.name && Number(row?.amount) > 0)) {
      toast.error("В рецепте нет рассчитанных продуктов"); return;
    }
    setSaving(true);
    try {
      await createWorkbookEntry({ type: "recipe_calculation", payload: {
        ...payload, order_id: item.order_id, order_item_id: item.order_item_id,
      }});
      const refreshed = await onSaved({ silent: true });
      const sections = ["ingredients", "packaging", "decor_supplies", "manual"];
      const appearsInList = sections.some((key) => (refreshed?.[key] || []).some(
        (row) => (row.sources || []).some((source) => source.order_item_id === item.order_item_id)
      ));
      const remainsUnresolved = (refreshed?.unresolved || []).some(
        (row) => row.order_item_id === item.order_item_id
      );
      if (!appearsInList || remainsUnresolved) {
        throw new Error("Продукты сохранены, но список покупок не обновился. Повторите попытку.");
      }
      onClose();
      toast.success("Продукты добавлены в список покупок", { duration: 3000 });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const openRecipes = async () => {
    setMode("recipes"); setSelectedRecipe(null); setPickerLoading(true);
    try {
      const [recipes, ingredients] = await Promise.all([listRecipes(), listIngredients()]);
      setEntries((recipes || []).filter((recipe) => recipe.is_active !== false));
      setIngredientNames(Object.fromEntries((ingredients || []).map((ingredient) => [ingredient.id, ingredient.name])));
    } catch (e) { toast.error(formatApiError(e)); setEntries([]); setIngredientNames({}); }
    finally { setPickerLoading(false); }
  };
  const chooseRecipe = async (id) => {
    if (busyRecipeId) return;
    setBusyRecipeId(id);
    try {
      const recipe = await getRecipe(id);
      const rows = (recipe?.ingredient_lines || []).map((line) => ({
        name: ingredientNames[line.ingredient_id] || "Ингредиент",
        amount: String(line.quantity),
        unit: line.unit,
      }));
      if (!rows.some((row) => row.name && Number(row.amount) > 0)) {
        toast.error("В рецептуре нет рассчитанных продуктов"); return;
      }
      setSelectedRecipe({
        title: recipe.name,
        source_ingredients: rows,
        recalculated_ingredients: [],
        technique: recipe.notes || null,
        source_shape: null,
        target_shape: null,
        cost: null,
        currency_code: "EUR",
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyRecipeId(null); }
  };
  const parsePaste = () => {
    const result = parseRecipe(paste);
    if (!result.items.length) { toast.error("Не удалось распознать продукты"); return; }
    setParsed(result);
  };
  const savePaste = () => savePayload({
    title: parsed.title,
    source_ingredients: parsed.items.map((row) => ({ name: row.name, amount: String(row.amount), unit: row.unit })),
    recalculated_ingredients: [], technique: parsed.technique || null,
    source_shape: null, target_shape: null, cost: null, currency_code: "EUR",
  });
  const fullUrl = `/master-order/recipe-recalc?order_id=${item.order_id}&position_id=${item.order_item_id}`;

  return <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(42,29,20,.38)" }} data-testid="mo-purchases-calculate-modal">
    <div className="w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-3xl bg-white p-5 md:p-6" style={{ boxShadow: "0 24px 70px rgba(42,29,20,.22)" }}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div><div className="text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>{`Заказ №${displayOrderNumber(item.order_number)} · Позиция ${item.order_item_position || "—"}`}</div>
          <div className="text-[12.5px] mt-0.5" style={{ color: "#6E5846" }}>{[item.product_type_name, item.flavor_composition].filter(Boolean).join(" · ")}</div></div>
        <button onClick={onClose} className="w-9 h-9 rounded-full grid place-items-center" aria-label="Закрыть"><X size={16} /></button>
      </div>
      <p className="text-[13px] mb-4" style={{ color: "#6E5846" }}>Для этой позиции пока не рассчитаны продукты.</p>

      {mode === "menu" && <div className="space-y-2">
        <button onClick={openRecipes} className="w-full h-11 rounded-xl flex items-center justify-center gap-2" style={{ background: "#4A3728", color: "#F7EFE0" }} data-testid="mo-purchases-choose-recipe"><BookOpen size={15} />Выбрать из моих рецептур</button>
        <button onClick={() => setMode("paste")} className="w-full h-11 rounded-xl flex items-center justify-center gap-2" style={{ color: "#4A3728", boxShadow: "inset 0 0 0 1px #C7A574" }} data-testid="mo-purchases-paste-recipe"><ClipboardPaste size={15} />Вставить рецепт</button>
      </div>}

      {mode === "recipes" && <div>
        <button onClick={() => { setMode("menu"); setSelectedRecipe(null); }} className="text-[12px] mb-3" style={{ color: "#8B6F4B" }}>← Назад</button>
        {pickerLoading ? <LoadingBlock label="Загружаем рецептуры..." /> : selectedRecipe ? <div data-testid="mo-purchases-recipe-preview">
          <div className="text-[15px] mb-2" style={{ color: "#2A1D14" }}>{selectedRecipe.title}</div>
          <div className="space-y-1.5 mb-3">{selectedRecipe.source_ingredients.map((row, index) => <div key={`${row.name}-${index}`} className="flex justify-between gap-3 rounded-lg px-3 py-2 text-[12.5px]" style={{ background: "#FAF7F1" }}><span>{row.name}</span><span>{fmtNum(Number(row.amount))} {unitLabel(row.unit)}</span></div>)}</div>
          <div className="flex gap-2">
            <button onClick={() => setSelectedRecipe(null)} className="h-10 px-4 rounded-xl" style={{ color: "#4A3728", boxShadow: "inset 0 0 0 1px #E4D9C4" }}>Выбрать другую</button>
            <button onClick={() => savePayload(selectedRecipe)} disabled={saving} className="h-10 flex-1 rounded-full" style={{ background: "#4A3728", color: "#F7EFE0" }} data-testid="mo-purchases-use-recipe">{saving ? "Сохраняем…" : "Использовать для этой позиции"}</button>
          </div>
        </div> : entries.length === 0 ? <div className="text-[13px] py-5 text-center" style={{ color: "#8B6F4B" }}>Сохранённых рецептур пока нет.</div> :
          <div className="space-y-2">{entries.map((entry) => <button key={entry.id} onClick={() => chooseRecipe(entry.id)} disabled={!!busyRecipeId} className="w-full rounded-xl px-3.5 py-3 text-left" style={{ background: "#FAF7F1", color: "#2A1D14" }} data-testid="mo-purchases-recipe-option">
            <span className="text-[13px]">{entry.name}</span><ArrowRight size={13} className="float-right mt-0.5" />
          </button>)}</div>}
      </div>}

      {mode === "paste" && <div>
        <button onClick={() => { setMode("menu"); setParsed(null); }} className="text-[12px] mb-3" style={{ color: "#8B6F4B" }}>← Назад</button>
        {!parsed ? <><textarea value={paste} onChange={(e) => setPaste(e.target.value)} rows={8} placeholder={'Например:\nСливки 600 г\nСахар 300 г'} className="w-full rounded-xl p-3 text-[13px] outline-none" style={{ boxShadow: "inset 0 0 0 1px #E4D9C4" }} data-testid="mo-purchases-recipe-paste" />
          <button onClick={parsePaste} className="mt-3 w-full h-10 rounded-xl" style={{ background: "#4A3728", color: "#F7EFE0" }}>Распознать</button></> : <>
          <div className="space-y-1.5 mb-3">{parsed.items.map((row, index) => <div key={`${row.name}-${index}`} className="flex justify-between gap-3 rounded-lg px-3 py-2 text-[12.5px]" style={{ background: "#FAF7F1" }}><span>{row.name}</span><span>{fmtNum(Number(row.amount))} {unitLabel(row.unit)}</span></div>)}</div>
          <button onClick={savePaste} disabled={saving} className="w-full h-10 rounded-xl" style={{ background: "#4A3728", color: "#F7EFE0" }} data-testid="mo-purchases-save-products">{saving ? "Сохраняем…" : "Сохранить продукты"}</button>
        </>}
      </div>}

      <button onClick={() => nav(fullUrl)} className="mt-4 w-full text-[12.5px] py-2" style={{ color: "#6E5846" }} data-testid="mo-purchases-open-full-recalc">Открыть полный расчёт</button>
    </div>
  </div>;
}

/* --------------------------- one shopping row -------------------------- */

function InlineHaveInput({ entry, canWrite, onSave }) {
  const displayUnit = preferredInputUnit(entry.base_unit, entry.required_quantity);
  const { value: haveValue } = humanize(entry.base_unit, entry.available_quantity);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(fmtNum(haveValue).replace(",", "."));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(fmtNum(haveValue).replace(",", "."));
  }, [haveValue, editing]);

  const commit = async () => {
    const cleaned = draft.replace(",", ".").trim();
    const n = cleaned === "" ? 0 : Number(cleaned);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Введите неотрицательное число");
      setDraft(fmtNum(haveValue).replace(",", "."));
      setEditing(false);
      return;
    }
    const base = toBaseUnit(entry.base_unit, n, displayUnit);
    if (base === null) { setEditing(false); return; }
    if (Number(base) === Number(entry.available_quantity)) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave({ available_quantity: String(base) });
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (!canWrite || !editing) {
    return (
      <button
        onClick={() => canWrite && setEditing(true)}
        className="text-left inline-flex items-baseline gap-1"
        style={{ color: canWrite ? "#4A3728" : "#8B6F4B", cursor: canWrite ? "text" : "default" }}
        data-testid="mo-purchases-have-view"
      >
        <span className="text-[12.5px] tabular-nums">
          {humanQty(entry.base_unit, entry.available_quantity)}
        </span>
        {canWrite && <span className="h-6 px-2 rounded-full inline-flex items-center text-[11px]" style={{ color: "#6E5846", background: "#F7F1E7", boxShadow: "inset 0 0 0 1px #E4D9C4" }}>Изменить</span>}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
        onBlur={commit}
        disabled={saving}
        className="h-8 w-20 px-2 rounded-lg bg-white outline-none text-[12.5px] tabular-nums"
        style={{ boxShadow: "inset 0 0 0 1px #C7A574", color: "#2A1D14" }}
        data-testid="mo-purchases-have-input"
      />
      <span className="text-[11.5px]" style={{ color: "#8B6F4B" }}>{displayUnit}</span>
    </span>
  );
}

function EntryRow({ entry, canWrite, period, onStateChanged, onRefresh }) {
  const nav = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const patchState = useCallback(async (payload) => {
    setSaving(true);
    try {
      const saved = await patchShoppingState({
        entity_type: entry.entity_type,
        entity_id: entry.entity_id,
        period_start: period.start,
        period_end: period.end,
        payload,
      });
      onStateChanged(entry.entity_type, entry.entity_id, saved);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }, [entry.entity_type, entry.entity_id, period.start, period.end, onStateChanged]);

  const togglePurchased = () => patchState({ purchased: !entry.purchased });

  const removeManual = async () => {
    if (!canWrite || entry.entity_type !== "manual") return;
    setSaving(true);
    try {
      await deleteManualPurchase({ entity_id: entry.entity_id, period_start: period.start, period_end: period.end });
      toast.success("Покупка удалена");
      await onRefresh({ silent: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const purchasedTone = entry.purchased ? { opacity: 0.72 } : {};

  return (
    <div
      className="rounded-2xl bg-white px-4 py-3.5 md:px-5 md:py-4"
      style={{
        boxShadow: entry.purchased
          ? "inset 0 0 0 1px #E4D9C4"
          : "inset 0 0 0 1px #EFE4D0",
        ...purchasedTone,
      }}
      data-testid="mo-purchases-row"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
        {/* Name */}
        <div className="min-w-0 md:flex-1">
          <div className="text-[14.5px] md:text-[15px] leading-snug truncate"
               style={{ color: "#2A1D14", fontFamily: "'Cormorant Garamond', serif" }}
               data-testid="mo-purchases-row-name">
            {entry.name}
          </div>
          <div className="mt-0.5 text-[11.5px]" style={{ color: "#8B6F4B" }}>
            {entry.entity_type === "manual" ? "Добавлено вручную" : entry.sources.length === 1
              ? "Для 1 заказа"
              : `Для ${entry.sources.length} заказ${entry.sources.length < 5 ? "ов" : "ов"}`}
          </div>
        </div>

        {/* Metrics on desktop: needed, have, to buy */}
        <div className="grid grid-cols-3 gap-3 md:gap-5 md:w-[420px] shrink-0">
          <div>
            <div className="text-[10px] tracking-[0.14em] uppercase" style={{ color: "#8B6F4B" }}>Нужно</div>
            <div className="text-[13px] tabular-nums" style={{ color: "#4A3728" }}
                 data-testid="mo-purchases-row-required">
              {humanQty(entry.base_unit, entry.required_quantity)}
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.14em] uppercase" style={{ color: "#8B6F4B" }}>У меня есть</div>
            <div data-testid="mo-purchases-row-have">
              <InlineHaveInput entry={entry} canWrite={canWrite} onSave={patchState} />
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.14em] uppercase" style={{ color: "#8B6F4B" }}>Докупить</div>
            <div
              className="text-[14px] md:text-[15px] tabular-nums"
              style={{ color: entry.purchased ? "#8B6F4B" : Number(entry.to_buy) === 0 ? "#4A3728" : "#7A4A16", fontWeight: 600 }}
              data-testid="mo-purchases-row-tobuy"
            >
              {humanQty(entry.base_unit, entry.to_buy)}
            </div>
          </div>
        </div>

        {/* Purchased toggle + expand */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={togglePurchased}
            disabled={!canWrite || saving}
            className="h-9 px-3 rounded-xl text-[12px] flex items-center gap-1.5 transition"
            style={{
              background: entry.purchased ? "#4A3728" : "#F7F1E7",
              color: entry.purchased ? "#F7EFE0" : "#4A3728",
              boxShadow: entry.purchased ? "none" : "inset 0 0 0 1px #EFE4D0",
              opacity: !canWrite ? 0.5 : 1,
              cursor: !canWrite ? "default" : "pointer",
            }}
            data-testid="mo-purchases-row-purchased"
          >
            {entry.purchased ? <Check size={13} /> : <div className="w-3.5 h-3.5 rounded-full" style={{ boxShadow: "inset 0 0 0 1px #C7A574" }} />}
            Куплено
          </button>
          {entry.sources.length > 0 && <button
            onClick={() => setExpanded((v) => !v)}
            className="h-9 w-9 rounded-full grid place-items-center transition"
            style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
            data-testid="mo-purchases-row-expand"
            aria-label={expanded ? "Скрыть источники" : "Показать источники"}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>}
          {entry.entity_type === "manual" && canWrite && (
            <button onClick={removeManual} disabled={saving}
                    className="h-9 w-9 rounded-full grid place-items-center"
                    style={{ background: "#FFF7F4", color: "#9B4A3C", boxShadow: "inset 0 0 0 1px #EED8D2" }}
                    aria-label="Удалить покупку" data-testid="mo-purchases-manual-delete">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3" style={{ borderTop: "1px dashed #E4D9C4" }} data-testid="mo-purchases-row-sources">
          <div className="text-[11px] tracking-[0.14em] uppercase mb-2" style={{ color: "#8B6F4B" }}>
            Из каких заказов
          </div>
          <ul className="space-y-1.5">
            {entry.sources.map((s, idx) => (
              <li key={`${s.order_item_id}-${idx}`}>
                <button
                  onClick={() => nav(`/master-order/orders/${s.order_id}`)}
                  className="w-full flex items-center gap-2 rounded-lg px-2.5 py-2 text-[12.5px] text-left transition hover:bg-[#FDF7EC]"
                  style={{ color: "#2A1D14" }}
                  data-testid="mo-purchases-row-source-link"
                >
                  <span className="truncate" style={{ color: "#4A3728" }}>{orderItemLabel(s)}</span>
                  <span style={{ color: "#8B6F4B" }}>·</span>
                  <span className="tabular-nums" style={{ color: "#4A3728" }}>
                    {humanQty(entry.base_unit, s.quantity_contribution)}
                  </span>
                  {s.order_item_quantity > 1 && (
                    <span style={{ color: "#8B6F4B" }}> · {s.order_item_quantity} шт. в заказе</span>
                  )}
                  <ArrowRight size={12} color="#8B6F4B" className="ml-auto" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* -------------------------------- section ------------------------------- */

function Section({ title, entries, canWrite, period, onStateChanged, onRefresh, filter }) {
  const visible = useMemo(() => {
    if (filter === "unpurchased") return entries.filter((e) => !e.purchased);
    if (filter === "purchased")   return entries.filter((e) => e.purchased);
    return entries;
  }, [entries, filter]);

  return (
    <section className="mt-5" data-testid={`mo-purchases-section-${title.toLowerCase().replaceAll(" ", "-")}`}>
      <div className="flex items-baseline justify-between mb-2 px-1">
        <h2 className="text-[18px] md:text-[20px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
          {title}
        </h2>
        <div className="text-[11.5px]" style={{ color: "#8B6F4B" }}>
          {visible.length === 0
            ? "нет позиций"
            : `${visible.length} ${visible.length === 1 ? "позиция" : visible.length < 5 ? "позиции" : "позиций"}`}
        </div>
      </div>
      {visible.length === 0 ? (
        <div className="rounded-2xl bg-white px-4 py-6 text-center text-[12.5px]"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#8B6F4B" }}>
          Здесь пока пусто.
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((entry) => (
            <EntryRow
              key={`${entry.entity_type}-${entry.entity_id}`}
              entry={entry}
              canWrite={canWrite}
              period={period}
              onStateChanged={onStateChanged}
              onRefresh={onRefresh}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/* --------------------------------- page --------------------------------- */

function purchasedCount(items) {
  return items.reduce((n, e) => n + (e.purchased ? 1 : 0), 0);
}

const MANUAL_UNITS = [
  ["g", "г"], ["kg", "кг"], ["ml", "мл"], ["l", "л"],
  ["pcs", "шт."], ["pack", "уп."], ["cm", "см"], ["m", "м"],
];
const MANUAL_CATEGORIES = [
  ["ingredient", "Ингредиенты"], ["packaging", "Упаковка"],
  ["decor_supplies", "Декор и расходники"], ["manual", "Добавлено вручную"],
];

function ManualPurchaseModal({ catalogs, period, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", quantity: "", unit: "pcs", category: "manual", catalog: "" });
  const [saving, setSaving] = useState(false);
  const chooseCatalog = (value) => {
    if (!value) { setForm((v) => ({ ...v, catalog: "" })); return; }
    const [type, id] = value.split(":");
    const row = (type === "ingredient" ? catalogs.ingredients : catalogs.packaging).find((x) => x.id === id);
    if (!row) return;
    setForm((v) => ({ ...v, catalog: value, name: row.name, unit: row.base_unit || "pcs", category: type }));
  };
  const submit = async (e) => {
    e.preventDefault();
    const quantity = Number(String(form.quantity).replace(",", "."));
    if (!form.name.trim() || !Number.isFinite(quantity) || quantity <= 0) {
      toast.error("Укажите название и количество больше нуля"); return;
    }
    setSaving(true);
    try {
      const [catalog_entity_type, catalog_entity_id] = form.catalog ? form.catalog.split(":") : [null, null];
      await createManualPurchase({ period_start: period.start, period_end: period.end, payload: {
        name: form.name.trim(), quantity: String(quantity), unit: form.unit, category: form.category,
        catalog_entity_type, catalog_entity_id,
      }});
      toast.success("Покупка добавлена");
      await onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  const fieldStyle = { boxShadow: "inset 0 0 0 1px #E4D9C4", color: "#2A1D14" };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(42,29,20,.38)" }} data-testid="mo-manual-modal">
      <form onSubmit={submit} className="w-full max-w-md rounded-3xl bg-white p-5 md:p-6" style={{ boxShadow: "0 24px 70px rgba(42,29,20,.22)" }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[22px]" style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>Добавить покупку</h2>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full grid place-items-center" aria-label="Закрыть"><X size={16} /></button>
        </div>
        <label className="block text-[12px] mb-3" style={{ color: "#6E5846" }}>
          Выбрать из справочника (необязательно)
          <select value={form.catalog} onChange={(e) => chooseCatalog(e.target.value)} className="mt-1 w-full h-10 rounded-xl px-3 bg-white" style={fieldStyle}>
            <option value="">Ввести название вручную</option>
            <optgroup label="Ingredients">{catalogs.ingredients.map((x) => <option key={x.id} value={`ingredient:${x.id}`}>{x.name}</option>)}</optgroup>
            <optgroup label="Packaging">{catalogs.packaging.map((x) => <option key={x.id} value={`packaging:${x.id}`}>{x.name}</option>)}</optgroup>
          </select>
        </label>
        <label className="block text-[12px] mb-3" style={{ color: "#6E5846" }}>Название
          <input value={form.name} onChange={(e) => setForm((v) => ({ ...v, name: e.target.value, catalog: "" }))} maxLength={160}
                 className="mt-1 w-full h-10 rounded-xl px-3 bg-white" style={fieldStyle} data-testid="mo-manual-name" />
        </label>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="text-[12px]" style={{ color: "#6E5846" }}>Количество
            <input value={form.quantity} onChange={(e) => setForm((v) => ({ ...v, quantity: e.target.value }))} inputMode="decimal"
                   className="mt-1 w-full h-10 rounded-xl px-3 bg-white" style={fieldStyle} data-testid="mo-manual-quantity" />
          </label>
          <label className="text-[12px]" style={{ color: "#6E5846" }}>Единица
            <select value={form.unit} onChange={(e) => setForm((v) => ({ ...v, unit: e.target.value }))} className="mt-1 w-full h-10 rounded-xl px-3 bg-white" style={fieldStyle}>
              {MANUAL_UNITS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
        </div>
        <label className="block text-[12px] mb-5" style={{ color: "#6E5846" }}>Категория
          <select value={form.category} onChange={(e) => setForm((v) => ({ ...v, category: e.target.value }))} className="mt-1 w-full h-10 rounded-xl px-3 bg-white" style={fieldStyle}>
            {MANUAL_CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-xl" style={{ color: "#4A3728", boxShadow: "inset 0 0 0 1px #E4D9C4" }}>Отмена</button>
          <button type="submit" disabled={saving} className="h-10 px-5 rounded-xl" style={{ background: "#4A3728", color: "#F7EFE0" }} data-testid="mo-manual-save">{saving ? "Сохраняем…" : "Добавить"}</button>
        </div>
      </form>
    </div>
  );
}

export default function MOPurchases() {
  const { mode, loading: accessLoading } = useMOAccess();
  const canWrite = isWritable(mode);

  const [preset, setPreset] = useState("7d");
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(addDaysISO(todayISO(), 6));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [resetting, setResetting] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [calculateItem, setCalculateItem] = useState(null);
  const [catalogs, setCatalogs] = useState({ ingredients: [], packaging: [] });

  const applyPreset = (id) => {
    setPreset(id);
    const today = todayISO();
    const p = PRESETS.find((x) => x.id === id);
    if (!p || p.days === null) return; // custom — user picks dates
    setStart(today);
    setEnd(addDaysISO(today, p.days));
  };
  const setCustom = (s, e) => {
    setStart(s);
    setEnd(e);
    setPreset("custom");
  };

  const load = useCallback(async ({ silent = false } = {}) => {
    if (start > end) return;
    if (!silent) setLoading(true);
    try {
      const body = await getShoppingList({ period_start: start, period_end: end });
      setData(body);
      return body;
    } catch (e) {
      toast.error(formatApiError(e));
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const doReset = async () => {
    if (!canWrite) return;
    setResetting(true);
    try {
      const { modified_count } = await resetShoppingPurchased({ period_start: start, period_end: end });
      toast.success(modified_count > 0 ? `Сброшено «Куплено» у ${modified_count} позиций` : "Нечего сбрасывать");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setResetting(false);
    }
  };

  const openManual = async () => {
    if (!canWrite) return;
    try {
      const [ingredientsRows, packagingRows] = await Promise.all([listIngredients(), listPackagingItems()]);
      setCatalogs({ ingredients: ingredientsRows || [], packaging: packagingRows || [] });
    } catch { setCatalogs({ ingredients: [], packaging: [] }); }
    setManualOpen(true);
  };

  const setItemExcluded = async (item, excluded) => {
    if (!canWrite) return;
    try {
      await updateOrderItem(item.order_item_id, { shopping_list_excluded: excluded });
      toast.success(excluded ? "Позиция не учитывается в списке покупок" : "Позиция возвращена в список покупок");
      await load({ silent: true });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyStatePatch = useCallback((entityType, entityId, saved) => {
    setData((current) => {
      if (!current) return current;
      const patchRows = (rows = []) => rows.map((row) => {
        if (row.entity_type !== entityType || row.entity_id !== entityId) return row;
        const available = saved?.available_quantity ?? row.available_quantity;
        return {
          ...row,
          available_quantity: available,
          purchased: saved?.purchased ?? row.purchased,
          to_buy: String(Math.max(Number(row.required_quantity) - Number(available), 0)),
        };
      });
      return {
        ...current,
        ingredients: patchRows(current.ingredients),
        packaging: patchRows(current.packaging),
        decor_supplies: patchRows(current.decor_supplies),
        manual: patchRows(current.manual),
      };
    });
  }, []);

  const ingredients = data?.ingredients || [];
  const packaging = data?.packaging || [];
  const decorSupplies = data?.decor_supplies || [];
  const manual = data?.manual || [];
  const unresolved = data?.unresolved || [];
  const excluded = data?.excluded || [];
  const purchasedTotal = purchasedCount(ingredients) + purchasedCount(packaging) + purchasedCount(decorSupplies) + purchasedCount(manual);

  const body = (() => {
    if (accessLoading) return <LoadingBlock label="Проверяем доступ..." />;
    if (mode === "no_access" || mode === "demo") return <NoAccessScreen />;

    return (
      <>
        <PeriodPicker
          preset={preset}
          onPreset={applyPreset}
          start={start}
          end={end}
          onCustom={setCustom}
          ordersCount={data?.summary?.orders_in_period ?? 0}
        />

        {loading ? (
          <div className="mt-4"><LoadingBlock label="Собираем список..." /></div>
        ) : (
          <>
            <div className="mt-4">
              <UnresolvedBlock items={unresolved} canWrite={canWrite} onCalculate={setCalculateItem} onExclude={(item) => setItemExcluded(item, true)} />
              <ExcludedBlock items={excluded} canWrite={canWrite} onRestore={(item) => setItemExcluded(item, false)} />
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-1 mb-1 px-1"
                 data-testid="mo-purchases-filter-bar">
              {[
                { id: "all",         label: "Все" },
                { id: "unpurchased", label: "Не куплено" },
                { id: "purchased",   label: "Куплено" },
              ].map((f) => {
                const active = filter === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className="h-8 px-3 rounded-xl text-[12px] transition"
                    style={{
                      background: active ? "#EADFCF" : "transparent",
                      color: active ? "#2A1D14" : "#8B6F4B",
                      boxShadow: active ? "inset 0 0 0 1px #C7A574" : "inset 0 0 0 1px transparent",
                    }}
                    data-testid={`mo-purchases-filter-${f.id}`}
                  >
                    {f.label}
                  </button>
                );
              })}

              <div className="ml-auto flex items-center gap-2">
                {canWrite && (
                  <button onClick={openManual} className="h-9 px-3.5 rounded-xl text-[12px] flex items-center gap-1.5"
                          style={{ background: "#4A3728", color: "#F7EFE0" }} data-testid="mo-purchases-add-manual">
                    <Plus size={13} /> Добавить покупку
                  </button>
                )}
                {purchasedTotal > 0 && canWrite && (
                  <button
                    onClick={doReset}
                    disabled={resetting}
                    className="h-9 px-3 rounded-xl text-[12px] flex items-center gap-1.5 transition"
                    style={{ background: "#FFFFFF", color: "#4A3728",
                             boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-purchases-reset"
                  >
                    <RotateCcw size={13} />
                    {resetting ? "Сбрасываем..." : "Сбросить куплено"}
                  </button>
                )}
              </div>
            </div>

            {ingredients.length === 0 && packaging.length === 0 && decorSupplies.length === 0 && manual.length === 0 && unresolved.length === 0 && excluded.length === 0 && (
              <div className="mt-4 rounded-2xl bg-white p-8 md:p-10 text-center"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                   data-testid="mo-purchases-empty">
                <div className="mx-auto w-12 h-12 rounded-full grid place-items-center mb-3"
                     style={{ background: "#EADFCF", color: "#4A3728" }}>
                  <ShoppingBag size={20} strokeWidth={1.7} />
                </div>
                <div className="text-[16px] md:text-[18px]"
                     style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
                     data-testid="mo-purchases-empty-title">
                  На выбранный период заказов пока нет
                </div>
                <div className="text-[12.5px] mt-1.5 mx-auto max-w-md" style={{ color: "#8B6F4B" }}
                     data-testid="mo-purchases-empty-subtitle">
                  Когда появятся рассчитанные заказы, список покупок сформируется автоматически. Покупку также можно добавить вручную.
                </div>
                <div className="mt-4 mx-auto max-w-md text-[11.5px] leading-relaxed"
                     style={{ color: "#8B6F4B" }} data-testid="mo-purchases-empty-flow">
                  Список покупок формируется автоматически из ваших заказов.
                  <div className="mt-1.5 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1"
                       style={{ color: "#4A3728" }}>
                    <span>Заказ</span><ArrowRight size={11} color="#B9A692" />
                    <span>рецепт</span><ArrowRight size={11} color="#B9A692" />
                    <span>потребность</span><ArrowRight size={11} color="#B9A692" />
                    <span>список покупок</span>
                  </div>
                </div>
              </div>
            )}

            {ingredients.length === 0 && packaging.length === 0 && decorSupplies.length === 0 && manual.length === 0 && unresolved.length > 0 && excluded.length === 0 && (
              <div className="mt-4 rounded-2xl bg-white p-8 md:p-10 text-center"
                   style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                   data-testid="mo-purchases-empty-unresolved">
                <div className="mx-auto w-12 h-12 rounded-full grid place-items-center mb-3"
                     style={{ background: "#F5EBD8", color: "#7A4A16" }}>
                  <ShoppingBag size={20} strokeWidth={1.7} />
                </div>
                <div className="text-[16px] md:text-[18px]"
                     style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
                     data-testid="mo-purchases-empty-unresolved-title">
                  Для этих позиций пока не рассчитаны продукты
                </div>
                <div className="text-[12.5px] mt-1.5 mx-auto max-w-md" style={{ color: "#8B6F4B" }}
                     data-testid="mo-purchases-empty-unresolved-subtitle">
                  Рассчитайте их или исключите из списка покупок.
                </div>
              </div>
            )}

            <Section title="Ингредиенты" entries={ingredients}
                     canWrite={canWrite} period={{ start, end }} onStateChanged={applyStatePatch} onRefresh={load} filter={filter} />
            <Section title="Упаковка" entries={packaging}
                     canWrite={canWrite} period={{ start, end }} onStateChanged={applyStatePatch} onRefresh={load} filter={filter} />
            <Section title="Декор и расходники" entries={decorSupplies}
                     canWrite={canWrite} period={{ start, end }} onStateChanged={applyStatePatch} onRefresh={load} filter={filter} />
            <Section title="Добавлено вручную" entries={manual}
                     canWrite={canWrite} period={{ start, end }} onStateChanged={applyStatePatch} onRefresh={load} filter={filter} />
          </>
        )}
      </>
    );
  })();

  return (
    <MOShell active="purchases" title="Список покупок"
             subtitle="Что нужно купить для предстоящих заказов">
      {body}
      {manualOpen && <ManualPurchaseModal catalogs={catalogs} period={{ start, end }} onClose={() => setManualOpen(false)} onSaved={() => load({ silent: true })} />}
      {calculateItem && <CalculateProductsModal item={calculateItem} onClose={() => setCalculateItem(null)} onSaved={load} />}
    </MOShell>
  );
}
